/**
 * Julie Josephine – search synonyms.
 *
 * Shopify's synonym feature is paused and its search never matched Norwegian
 * at all: "genser", "skjorte", "bukse" and "kjole" all return nothing. Liquid
 * cannot fix that after the fact — it only ever sees what Shopify matched — so
 * the query is rewritten before the request goes out.
 *
 * Two mechanisms, deliberately distinct:
 *
 *   SYNONYMS are a fallback. The shopper's own words are tried first, and a
 *   synonym is used only when that comes back empty — with the substitution
 *   stated in the UI. Exact matches are never hijacked.
 *
 *   WIDENINGS run every time and return both sets. "polo" is the case: in
 *   Norwegian it means turtleneck, but the catalogue has a real Benedicte
 *   Polo, so a shopper typing it gets the polo AND the turtlenecks.
 *
 *   MATERIALS do not go through search at all. The fabric lives in the
 *   custom.materials metafield, which the search index never sees, so "wool"
 *   used to find only the styles with the word in their name. A material word
 *   is answered by the storefront filter instead —
 *   /collections/all?filter.p.m.custom.materials=Wool — and those products are
 *   merged into the shopper's own results. Like a widening, it runs every time.
 *
 * Vocabulary lives in snippets/jj-search-synonyms.liquid.
 */
(function () {
  'use strict';

  var VOCAB_ID = 'jj-search-synonyms';
  var EXPAND_ID = 'jj-search-expansions';
  var LINKS_ID = 'jj-search-links';
  var MATERIALS_ID = 'jj-search-materials';
  var COLOURS_ID = 'jj-search-colours';
  var MARKER = 'jj_syn';
  var GRID_MARKER = 'jj-ps-grid';

  /* The Material (filter) metafield, as the storefront filter names it. */
  var MATERIAL_PARAM = 'filter.p.m.custom.materials';
  var MATERIAL_MAX = 6;

  /* The collection template's product section, used to fetch cards cheaply. */
  var COLLECTION_SECTION = 'main-collection-premium';

  var NAMES_ID = 'jj-search-names';

  /* --------------------------------------------------- language and paths */

  /*
   * Every path the script asks for goes through the storefront root, which
   * is "/" in English and "/no/" on the Norwegian site, so a fetch made on
   * /no answers in Norwegian and a link keeps the shopper there.
   */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Text the page writes, in the shopper's language (snippets/jj-strings.liquid). */
  function str(key, fallback, vars) {
    var s = (window.jjStrings || {})[key] || fallback;
    if (typeof window.jjFormat === 'function') return window.jjFormat(s, vars);
    return String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, name) {
      return vars && vars[name] != null ? vars[name] : m;
    });
  }

  /*
   * A colour name as the page shows it ("Svart" on /no) to the colour key
   * every data-* attribute carries ("black" in every language). Without the
   * jj-strings helper it falls back to the English handle, which is the same
   * thing on the English site.
   */
  function keyOf(name) {
    if (typeof window.jjColourKey === 'function') return window.jjColourKey(name);
    return normalise(name).replace(/ /g, '-');
  }

  /*
   * True where colour names are translated (the colour-key map has entries).
   * There Shopify's index may hold "Svart" rather than "Black", so a colour
   * is asked for in the shopper's word first and the catalogue's second. On
   * the English site the map is empty and nothing about the request changes.
   */
  function coloursTranslated() {
    var map = (window.jjStrings || {}).colour_keys;
    return typeof map === 'string' && map.replace(/\|/g, '') !== '';
  }

  var names = null;

  /* Display names for the canonical colours and fabrics, from the snippet. */
  function loadNames() {
    if (names) return names;
    names = { colours: {}, fabrics: {} };
    var el = document.getElementById(NAMES_ID);
    if (!el) return names;
    try {
      var parsed = JSON.parse(el.textContent) || {};
      names.colours = parsed.colours || {};
      names.fabrics = parsed.fabrics || {};
    } catch (err) { /* keep the English words */ }
    return names;
  }

  function colourName(colour) {
    return loadNames().colours[colour.word] || colour.word;
  }

  function fabricName(material) {
    return loadNames().fabrics[material.word] || material.word;
  }

  /* The words to search a colour by, in the order to try them. */
  function colourSearchWords(colour) {
    if (!coloursTranslated()) return [colour.word];
    var shown = colourName(colour);
    return shown && shown !== colour.word ? [shown, colour.word] : [colour.word];
  }

  /* ---------------------------------------------------------------- vocab */

  var aliasToCanonical = null;
  var aliasPhrases = [];

  /* Fold the Norwegian vowels and punctuation so "blaa", "blå" and "bla" all
     reach the same key, whichever way the shopper spells it. */
  function normalise(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[æ]/g, 'ae')
      .replace(/[øö]/g, 'o')
      .replace(/[åä]/g, 'a')
      .replace(/[éè]/g, 'e')
      .replace(/[-_/]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function loadVocab() {
    if (aliasToCanonical) return aliasToCanonical;
    aliasToCanonical = {};

    var el = document.getElementById(VOCAB_ID);
    if (!el) return aliasToCanonical;

    var map;
    try {
      map = JSON.parse(el.textContent);
    } catch (err) {
      return aliasToCanonical;
    }

    Object.keys(map).forEach(function (canonical) {
      var aliases = map[canonical] || [];
      for (var i = 0; i < aliases.length; i++) {
        var key = normalise(aliases[i]);
        if (!key || aliasToCanonical[key]) continue;
        aliasToCanonical[key] = canonical;
        if (key.indexOf(' ') !== -1) aliasPhrases.push(key);
      }
    });

    /* Longest phrases first, so "t skjorte" wins over "skjorte". */
    aliasPhrases.sort(function (a, b) { return b.length - a.length; });
    return aliasToCanonical;
  }

  var expansions = null;

  /* Widenings are matched on the whole query, so they stay predictable. */
  function loadExpansions() {
    if (expansions) return expansions;
    expansions = {};

    var el = document.getElementById(EXPAND_ID);
    if (!el) return expansions;

    var map;
    try {
      map = JSON.parse(el.textContent);
    } catch (err) {
      return expansions;
    }

    Object.keys(map).forEach(function (term) {
      var key = normalise(term);
      if (key) expansions[key] = map[term] || [];
    });
    return expansions;
  }

  var links = null;

  /*
   * Some searches want a page, not a product — "sale" and "salg" were the most
   * searched terms in the no-results report after product names. Offer the
   * link; never navigate on the shopper's behalf.
   */
  function loadLinks() {
    if (links) return links;
    links = [];

    var el = document.getElementById(LINKS_ID);
    if (!el) return links;

    try {
      links = (JSON.parse(el.textContent) || []).filter(function (entry) {
        /* A page that does not exist gives no URL; offer nothing rather than a dead link. */
        return entry && entry.url;
      }).map(function (entry) {
        return {
          url: entry.url,
          label: entry.label,
          terms: (entry.terms || []).map(normalise)
        };
      });
    } catch (err) {
      links = [];
    }
    return links;
  }

  function linkFor(query) {
    var text = normalise(query);
    if (!text) return null;
    var all = loadLinks();
    for (var i = 0; i < all.length; i++) {
      if (all[i].terms.indexOf(text) !== -1) return all[i];
    }
    return null;
  }

  function linkHtml(entry) {
    return '<a class="jj-search-link" href="' + escapeHtml(entry.url) + '">' +
      escapeHtml(entry.label) + '</a>';
  }

  /* "polo" -> "polo OR turtleneck", which the suggest endpoint understands. */
  function widen(query) {
    var extra = loadExpansions()[normalise(query)];
    if (!extra || !extra.length) return null;
    return { query: [String(query).trim()].concat(extra).join(' OR '), extra: extra };
  }

  /* ------------------------------------------------------------ materials */

  var materials = null;      /* word  -> { word, values } */
  var materialAlias = null;  /* alias -> word            */

  function loadMaterials() {
    if (materials) return materials;
    materials = {};
    materialAlias = {};

    var el = document.getElementById(MATERIALS_ID);
    if (!el) return materials;

    var map;
    try {
      map = JSON.parse(el.textContent);
    } catch (err) {
      return materials;
    }

    Object.keys(map).forEach(function (word) {
      var entry = map[word] || {};
      var values = entry.values || [];
      if (!values.length) return;

      var key = normalise(word);
      materials[key] = { word: word, values: values };

      (entry.aliases || []).forEach(function (alias) {
        var a = normalise(alias);
        if (a && !materialAlias[a]) materialAlias[a] = key;
      });
    });
    return materials;
  }

  /*
   * Matched on the WHOLE query, like a widening. "wool" is a fabric question;
   * "wool trousers" is not — Shopify answers that one well enough on its own,
   * and filtering to every wool product would bury the trousers.
   */
  function materialFor(query) {
    loadMaterials();
    var key = normalise(query);
    if (!key) return null;
    return materials[key] || materials[materialAlias[key]] || null;
  }

  /* Repeated filter params are OR'd, so a blend is reached by listing it. */
  function materialUrl(entry, sectionId, colour) {
    var parts = entry.values.map(function (value) {
      return MATERIAL_PARAM + '=' + encodeURIComponent(value);
    });
    if (sectionId) parts.push('section_id=' + encodeURIComponent(sectionId));

    /*
     * With a colour in the query, ask through SEARCH rather than the
     * collection: filters narrow a search too, and the drawer section already
     * knows how to read the typed colour and preselect that colourway. Asking
     * the collection would return the same products showing whichever colour
     * they default to.
     */
    if (colour) {
      return root() + 'search?q=' + encodeURIComponent(colourSearchWords(colour)[0]) + '&' + parts.join('&');
    }
    return root() + 'collections/all?' + parts.join('&');
  }

  /*
   * A colourway matches when the asked-for colour is the head of its name:
   * "white" covers White Pinstripe, "grey" covers Grey Melange, and neither
   * reaches Off White or Light Grey — those are colours of their own.
   */
  function colourMatches(handle, colour) {
    if (!handle) return false;
    return handle === colour.handle || handle.indexOf(colour.handle + '-') === 0;
  }

  /* ------------------------------------------------------------- colours */

  var colours = null;

  function loadColours() {
    if (colours) return colours;
    colours = {};

    var el = document.getElementById(COLOURS_ID);
    if (!el) return colours;

    var list;
    try {
      list = JSON.parse(el.textContent) || [];
    } catch (err) {
      return colours;
    }

    list.forEach(function (name) {
      var key = normalise(name);
      if (key) colours[key] = { word: key, handle: key.replace(/ /g, '-') };
    });
    return colours;
  }

  /* "svart" is a colour because the synonyms already know it means "black". */
  function colourFor(term) {
    loadColours();
    var key = normalise(term);
    if (!key) return null;
    if (colours[key]) return colours[key];

    var canonical = normalise(loadVocab()[key] || '');
    return colours[canonical] || null;
  }

  /* --------------------------------------------------------- the query */

  /*
   * What did the shopper actually ask for? A fabric, a colour, a name, or a
   * couple of those at once. Longest phrases first, so "off white" is read as
   * one colour and "merino wool" as one fabric.
   */
  function readQuery(query) {
    var words = normalise(query).split(' ').filter(Boolean);
    var found = { material: null, colour: null, rest: [] };
    var i = 0;

    while (i < words.length) {
      var pair = i + 1 < words.length ? words[i] + ' ' + words[i + 1] : null;

      if (pair && take(found, pair)) { i += 2; continue; }
      if (take(found, words[i])) { i += 1; continue; }

      found.rest.push(words[i]);
      i += 1;
    }

    return found;
  }

  function take(found, term) {
    if (!found.material) {
      var material = materialFor(term);
      if (material) { found.material = material; return true; }
    }
    if (!found.colour) {
      var colour = colourFor(term);
      if (colour) { found.colour = colour; return true; }
    }
    return false;
  }

  /*
   * The fabric filter is only asked for when nothing else is in the query but
   * a fabric and, at most, a colour. "wool trousers" is a different question —
   * Shopify's own results answer it better than a list of everything woollen.
   */
  function materialQuery(query) {
    var read = readQuery(query);
    if (!read.material || read.rest.length) return null;
    return read;
  }

  /*
   * One line under the heading, saying what the page is showing, in the
   * shopper's language whatever language they typed in: "svart ull" and
   * "black wool" both read "everything we make in black wool" in English and
   * "alt vi lager i svart ull" on /no. The shape stays the same across every
   * kind of search so the page reads the same way each time.
   */
  function ledeFor(query) {
    var read = readQuery(query);

    if (!read.rest.length && (read.material || read.colour)) {
      return str('search.everything_in', 'Everything we make in {{ what }}', { what: phraseFor(read) });
    }

    return matchingLine(query);
  }

  function matchingLine(query) {
    return str('search.everything_matching', 'Everything matching \u201c{{ terms }}\u201d',
      { terms: String(query).trim() });
  }

  function setLede(text) {
    var lede = document.querySelector('[data-jj-search-lede]');
    if (!lede) return;
    lede.textContent = text;
    lede.classList.add('is-set');
  }

  /*
   * A fabric or colour query is answered after the page has painted, and
   * without this the shopper sees "nothing found" for the half-second it
   * takes. The class goes on <html> the moment this script runs — before the
   * results section has even been parsed — and the stylesheet swaps the panel
   * for a placeholder grid until the answer lands. A timer takes it off in
   * any case, so a failed fetch never leaves the page hidden.
   */
  var PENDING = 'jj-search-pending';

  function markPending() {
    if (location.pathname.indexOf('/search') === -1) return;

    var typed = getParam(location.search, 'q');
    if (!typed) return;

    var read = readQuery(typed);
    if (read.rest.length || (!read.material && !read.colour)) return;

    document.documentElement.classList.add(PENDING);
    setTimeout(settle, 8000);
  }

  function settle() {
    document.documentElement.classList.remove(PENDING);
  }

  /* Drop the products the asked-for colourway does not come in. */
  function narrowToColour(root, colour, cardSelector, swatchTest) {
    var kept = 0;
    root.querySelectorAll(cardSelector).forEach(function (card) {
      if (swatchTest(card, colour)) { kept += 1; return; }
      card.remove();
    });
    return kept;
  }

  /*
   * Which colourway is this card? A card that IS one colourway says so, and
   * that is the whole answer: its swatch row lists the style's other colours,
   * so reading those would keep the black tee on a page about white ones. A
   * card standing for the style reports what it is showing, and its chips
   * carry the rest.
   */
  function collectionCardHasColour(card, colour) {
    var own = card.getAttribute('data-pcx-color');
    if (own) return colourMatches(own, colour);

    if (colourMatches(card.getAttribute('data-pcx-shown'), colour)) return true;

    var swatches = card.querySelectorAll('[data-pcx-color]');
    for (var i = 0; i < swatches.length; i++) {
      if (colourMatches(swatches[i].getAttribute('data-pcx-color'), colour)) return true;
    }
    return false;
  }

  /*
   * The drawer's swatches carry the colour's displayed name ("Svart" on /no),
   * so each is turned into its colour key before the comparison: the key is
   * the English handle in every language, the same as the canonical's.
   */
  function drawerCardHasColour(card, colour) {
    var swatches = card.querySelectorAll('.jj-ps-swatch[title]');
    for (var i = 0; i < swatches.length; i++) {
      if (colourMatches(keyOf(swatches[i].getAttribute('title')), colour)) return true;
    }

    /* A style that comes in one colour shows no swatches: the radio names it. */
    var radio = card.querySelector('.jj-ps-radio');
    if (radio) {
      return colourMatches(keyOf(radio.getAttribute('aria-label')), colour);
    }
    return false;
  }

  /* The handle is what tells two renders of the same product apart. */
  function handleOf(node) {
    var link = node.querySelector('a[href*="/products/"]');
    var m = link && /\/products\/([a-z0-9-]+)/.exec(link.getAttribute('href') || '');
    return m ? m[1] : null;
  }

  /*
   * On the results page a card is a colourway, not a style — the same as on a
   * collection page — so the black sweater and the grey one are two cards of
   * one product and both belong. Keying the dedupe on the handle alone would
   * keep whichever came first and drop the rest.
   */
  function cardKey(node) {
    var handle = handleOf(node);
    if (!handle) return null;
    return handle + '/' + (node.getAttribute('data-pcx-color') || '');
  }

  /* Returns the rewritten query, or null when nothing in it is a known alias. */
  function rewrite(query) {
    var vocab = loadVocab();
    var text = normalise(query);
    if (!text) return null;

    var changed = false;

    for (var i = 0; i < aliasPhrases.length; i++) {
      var phrase = aliasPhrases[i];
      if (text === phrase || text.indexOf(phrase + ' ') === 0 ||
          text.indexOf(' ' + phrase) === text.length - phrase.length - 1) {
        text = text.split(phrase).join(vocab[phrase]);
        changed = true;
      }
    }

    var words = text.split(' ').map(function (w) {
      if (vocab[w]) { changed = true; return vocab[w]; }
      return w;
    });

    if (!changed) return null;
    var out = words.join(' ').replace(/\s+/g, ' ').trim();
    return out && out !== normalise(query) ? out : null;
  }

  /* ------------------------------------------------------------ url utils */

  function getParam(url, key) {
    var m = new RegExp('[?&]' + key + '=([^&]*)').exec(url);
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : '';
  }

  function setParam(url, key, value) {
    var encoded = encodeURIComponent(value);
    if (new RegExp('[?&]' + key + '=').test(url)) {
      return url.replace(new RegExp('([?&]' + key + '=)[^&]*'), '$1' + encoded);
    }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + key + '=' + encoded;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ------------------------------------------------------------ the drawer */

  function htmlResponse(html) {
    return new Response(html, {
      status: 200,
      headers: { 'Content-Type': 'text/html; charset=utf-8' }
    });
  }

  /*
   * Fold the material products into the drawer the shopper is already looking
   * at. Their own results keep the top of the list; the fabric fills the rest.
   * Both halves are the same section rendered twice, so the cards drop in as
   * they are and the CSS-only swatch switching keeps working.
   */
  function mergeMaterial(plainHtml, materialHtml, typed, read, pageCount) {
    var plain = new DOMParser().parseFromString(plainHtml, 'text/html');
    var extra = new DOMParser().parseFromString(materialHtml, 'text/html');

    /*
     * The fabric half came back filtered and, when a colour was asked for,
     * already showing it. What is left is to drop anything the colour does
     * not actually come in — a card can be returned for a Draft colourway
     * whose name merely matches.
     *
     * The shopper's own half needs the same test: "white cotton" matches the
     * Cecilia Cotton Trousers on the word cotton alone, and they come in sand.
     */
    var colour = read.colour;
    if (colour) {
      narrowToColour(plain, colour, '.jj-ps-card', drawerCardHasColour);
      if (!narrowToColour(extra, colour, '.jj-ps-card', drawerCardHasColour)) return null;
    }

    /*
     * The shopper's own half can be emptied by the colour test. Take its
     * wrapper out rather than leaving an orphaned count above the results.
     */
    var wrap = plain.querySelector('.jj-ps');
    if (wrap && !wrap.querySelector('.jj-ps-card')) {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      wrap = null;
    }

    /*
     * Nothing matched the typed words, so the fabric answers on its own and
     * takes the place of the "no results" line. The section wrapper Shopify
     * put around the response has to survive: the drawer looks for it by id
     * and swaps in what it finds, so a reply without it renders as nothing.
     */
    if (!wrap) {
      var found = extra.querySelector('.jj-ps');
      if (!found) return null;

      wrap = plain.importNode(found, true);
      var none = plain.querySelector('.predictive-search__no-results');
      if (none && none.parentNode) {
        none.parentNode.replaceChild(wrap, none);
      } else {
        (plain.querySelector('.shopify-section') || plain.body).appendChild(wrap);
      }
    }

    var grid = wrap.querySelector('.' + GRID_MARKER);
    if (!grid) return null;

    var seen = {};
    var cards = grid.querySelectorAll('.jj-ps-card');
    for (var i = 0; i < cards.length; i++) {
      var h = handleOf(cards[i]);
      if (h) seen[h] = true;
    }
    var count = cards.length;

    extra.querySelectorAll('.jj-ps-card').forEach(function (card) {
      var handle = handleOf(card);
      if (!handle || seen[handle]) return;
      if (count >= MATERIAL_MAX) return;
      seen[handle] = true;
      count += 1;
      grid.appendChild(plain.importNode(card, true));
    });

    if (!count) return null;

    var total = pageCount || Math.max(totalOf(plain, read), totalOf(extra, read), count);
    setCount(wrap, total, phraseFor(read));
    setViewAll(wrap, typed);

    return plain.body.innerHTML;
  }

  /*
   * What the shopper asked for, in the words the page heading uses and in the
   * shopper's language: "black wool", "svart ull". A colour and a fabric are
   * joined by a locale key, so the order can differ by language.
   */
  function phraseFor(read) {
    if (!read || read.rest.length) return '';
    var fabric = read.material ? fabricName(read.material) : '';
    if (!read.colour) return fabric;
    var colour = colourName(read.colour);
    if (!fabric) return colour;
    return str('search.colour_fabric', '{{ colour }} {{ fabric }}', { colour: colour, fabric: fabric });
  }

  /*
   * The section counts everything it could show, not the six it drew — and
   * counts it twice, because the results page lists colourways for a fabric
   * (three Thale trench coats) and styles for a colour (the black one).
   */
  function totalOf(doc, read) {
    var wrap = doc.querySelector('.jj-ps');
    if (!wrap) return 0;

    /* For a colour: styles that come in it, by the page's own head-of-name rule. */
    var attr = read && read.colour ? 'data-jj-hits' : 'data-jj-colors';
    var n = parseInt(wrap.getAttribute(attr), 10);
    if (!(n > 0)) n = parseInt(wrap.getAttribute('data-jj-total'), 10);
    return n > 0 ? n : 0;
  }

  /*
   * The drawer draws six; the count says how many there are. "6 results" over
   * a fabric a shopper owns thirty of is worse than no count at all.
   */
  /*
   * The colour the section's own count named, if any, comes from its
   * data-jj-in attribute, never from reading the count's text back.
   */
  function setCount(wrap, total, phrase) {
    var meta = wrap.querySelector('.jj-ps-count');
    if (!meta) return;

    var what = phrase || meta.getAttribute('data-jj-in') || '';
    var one = total === 1;
    var text;
    if (what) {
      meta.setAttribute('data-jj-in', what);
      text = one
        ? str('search.count_in_one', '{{ number }} result in {{ what }}', { number: total, what: what })
        : str('search.count_in_other', '{{ number }} results in {{ what }}', { number: total, what: what });
    } else {
      text = one
        ? str('search.count_one', '{{ number }} result', { number: total })
        : str('search.count_other', '{{ number }} results', { number: total });
    }
    meta.textContent = text;
  }

  /*
   * The way on sits beside the count and under the last row, and both point at
   * the words the shopper typed. The material half is fetched under a query of
   * our own — "white" for "white cotton" — so its own link would send them
   * somewhere narrower than they asked for.
   */
  function setViewAll(wrap, typed) {
    var doc = wrap.ownerDocument;
    var href = root() + 'search?q=' + encodeURIComponent(String(typed).trim()) + '&type=product';

    /* A fabric render draws no link of its own; its wrapper carries the label. */
    var existing = wrap.querySelector('.jj-ps-viewall');
    var label = existing && existing.textContent.trim()
      ? existing.textContent.trim()
      : (wrap.getAttribute('data-label-view-all') || 'View all results');

    var meta = wrap.querySelector('.jj-ps-meta');
    if (meta && !meta.querySelector('.jj-ps-viewall')) {
      meta.appendChild(makeViewAll(doc, 'jj-ps-viewall', label));
    }

    var grid = wrap.querySelector('.' + GRID_MARKER);
    if (grid && !wrap.querySelector('.jj-ps-viewall--bottom')) {
      grid.insertAdjacentElement(
        'afterend',
        makeViewAll(doc, 'jj-ps-viewall jj-ps-viewall--bottom', label)
      );
    }

    wrap.querySelectorAll('.jj-ps-viewall').forEach(function (link) {
      link.setAttribute('href', href);
    });
  }

  function makeViewAll(doc, className, label) {
    var link = doc.createElement('a');
    link.className = className;
    link.setAttribute('data-instant', '');
    link.textContent = label;
    return link;
  }

  function patchFetch() {
    if (typeof window.fetch !== 'function' || typeof window.Response !== 'function') return;

    var nativeFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url);

      /* Only the predictive-search drawer request; everything else untouched. */
      if (!url || url.indexOf('/search/suggest') === -1 ||
          url.indexOf('section_id=predictive-search') === -1) {
        return nativeFetch(input, init);
      }

      var typed = getParam(url, 'q');
      var link = linkFor(typed);
      var lead = link ? linkHtml(link) : '';

      /*
       * A fabric word never reaches Shopify's index, so it is answered by the
       * material filter and merged in. Runs alongside the shopper's own
       * results, every time — the same bargain a widening makes.
       */
      /*
       * A colour on its own needs no fabric fetch, but it does need the same
       * test: Shopify matches a style on a tag or a description, and the card
       * comes back in whatever colour it defaults to.
       */
      var asked = readQuery(typed);
      if (asked.colour && !asked.material && !asked.rest.length) {
        /*
         * Ask SEARCH, not the suggest endpoint: suggest hands back ten products
         * at most, so the section could never count past them and "hvit" read
         * "6 results" over a page of twenty-three. And ask in the word the
         * catalogue uses — "svart" is black. Where colour names are
         * translated the shopper's word ("svart") goes first and the
         * catalogue's second; in English there is only the one word.
         */
        var colourWords = colourSearchWords(asked.colour);

        var askColour = function (i) {
          var colourUrl = root() + 'search?q=' + encodeURIComponent(colourWords[i]) +
            '&type=product&section_id=predictive-search';

          return nativeFetch(colourUrl, init)
            .then(function (r) { return r.text(); })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var kept = narrowToColour(doc, asked.colour, '.jj-ps-card', drawerCardHasColour);
              if (!kept) {
                if (i + 1 < colourWords.length) return askColour(i + 1);
                return htmlResponse(lead + html);
              }

              var wrap = doc.querySelector('.jj-ps');
              if (wrap) {
                setCount(wrap, Math.max(totalOf(doc, asked), kept), phraseFor(asked));
                setViewAll(wrap, typed);
              }
              return htmlResponse(lead + doc.body.innerHTML);
            });
        };

        return askColour(0).catch(function () { return nativeFetch(input, init); });
      }

      var read = materialQuery(typed);
      if (read) {
        /*
         * With a colour in the query the cards come through search, which
         * preselects the colourway, but the COUNT cannot: the fabric filter on
         * a search misses half the fabric — "Wool" returns nothing there while
         * "Merino Wool" returns ten — so that number would be the drawer's
         * word against the page's. Count what the page will show instead, from
         * the same fetch it makes. It fires only once the whole query reads as
         * a colour and a fabric, not on every keystroke.
         */
        return Promise.all([
          nativeFetch(input, init).then(function (r) { return r.text(); }),
          nativeFetch(materialUrl(read.material, 'predictive-search', read.colour), init)
            .then(function (r) { return r.text(); })
            .catch(function () { return ''; }),
          read.colour ? fabricCards(read) : Promise.resolve(null)
        ]).then(function (all) {
          var pageCount = all[2] ? all[2].querySelectorAll('article.pcx-card').length : 0;
          var merged = mergeMaterial(all[0], all[1], typed, read, pageCount);
          return htmlResponse(lead + (merged || all[0]));
        }).catch(function () { return nativeFetch(input, init); });
      }

      var wide = widen(typed);

      /*
       * A widening runs instead of the plain query, not after it: both sets
       * are wanted every time, so there is nothing to wait and see about.
       * If it comes back empty we fall through to the plain term.
       */
      if (wide) {
        return nativeFetch(setParam(url, 'q', wide.query), init)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            if (html.indexOf(GRID_MARKER) !== -1) return htmlResponse(lead + html);
            return nativeFetch(input, init);
          })
          .catch(function () { return nativeFetch(input, init); });
      }

      var alt = rewrite(typed);
      if (!alt && !lead) return nativeFetch(input, init);

      return nativeFetch(input, init).then(function (res) {
        return res.text().then(function (html) {
          /* The shopper's own words found something — leave well alone. */
          if (html.indexOf(GRID_MARKER) !== -1 || !alt) return htmlResponse(lead + html);

          return nativeFetch(setParam(url, 'q', alt), init)
            .then(function (r2) { return r2.text(); })
            .then(function (altHtml) {
              if (altHtml.indexOf(GRID_MARKER) === -1) return htmlResponse(lead + html);
              return htmlResponse(lead + altHtml);
            })
            .catch(function () { return htmlResponse(lead + html); });
        });
      });
    };
  }

  /* ------------------------------------------------------ the results page */

  function handleResultsPage() {
    if (location.pathname.indexOf('/search') === -1) return;

    var typed = getParam(location.search, 'q');
    if (!typed) return;

    /*
     * A search that has already been retried carries the canonical word, which
     * is what the page should describe — the shopper gets the English answer,
     * not a report of the word that failed. Everything below still applies to
     * it; only the retry itself must not happen twice.
     */
    var retried = getParam(location.search, MARKER) !== '';

    setLede(ledeFor(typed));

    var link = linkFor(typed);
    if (link) {
      var head = document.querySelector('.jj-search-head');
      if (head && !document.querySelector('.jj-search-link')) {
        head.insertAdjacentHTML('beforeend', linkHtml(link));
      }
    }

    var wrapper = document.querySelector('.search-results__wrapper');
    var hasProducts = wrapper && wrapper.querySelectorAll('a[href*="/products/"]').length > 0;

    /*
     * A colour in the query narrows whatever the page already rendered, fabric
     * or no fabric. "svart" arrives here as "black" after the retry, and the
     * grey ponchos Shopify returned alongside the black one still have to go.
     */
    var asked = readQuery(typed);
    if (asked.colour && !asked.rest.length) narrowPageToColour(asked.colour);

    var read = materialQuery(typed);
    if (read) {
      appendMaterial(read, typed).then(function (added) {
        settle();
        if (added) return;

        /* The fabric came back empty, or nothing in it comes in that colour. */
        if (hasProducts) {
          setLede(matchingLine(typed));
          return;
        }
        if (retried || !redirectToSynonym(typed)) reportNothing(typed);
      });
      return;
    }

    /* Colour needed no fetch; the narrowing above was the whole of the work. */
    settle();

    /* The results page has no OR, so the extra sets are fetched and appended. */
    var wide = widen(typed);
    if (wide) {
      appendWidened(wide.extra);
      if (hasProducts) return;
    }

    if (hasProducts) return;

    /* A synonym gets one more try; if there isn't one, say so plainly. */
    if (retried || !redirectToSynonym(typed)) reportNothing(typed);
  }

  /*
   * Nothing to show. The heading carries the news — the panel below it repeats
   * it in smaller type, so that half goes, and the search box stays.
   */
  function reportNothing(typed) {
    setLede(str('search.nothing_matching', 'Nothing matching \u201c{{ terms }}\u201d',
      { terms: String(typed).trim() }));

    var empty = document.querySelector('.empty-state');
    if (!empty) return;

    /* Whichever of the two panels is standing, its copy repeats the heading. */
    var prose = empty.querySelector('.prose');
    if (prose) prose.remove();

    var line = empty.querySelector(':scope > .v-stack > p');
    if (line) line.remove();
  }

  /*
   * The results page renders a card per colourway, so a search for "white"
   * comes back with the black Anette Tee alongside the white one — Shopify
   * matched the style, not the colour. Under a heading that says "everything
   * we make in white" that is simply wrong, so the other colourways go.
   *
   * Nothing is removed unless something survives: a colour we hold no stock
   * in should leave the shopper Shopify's answer rather than a blank page.
   */
  function narrowPageToColour(colour) {
    var grid = document.querySelector('.pcx__grid');
    if (!grid) return 0;

    var cards = grid.querySelectorAll('article.pcx-card');
    var keep = [];
    for (var i = 0; i < cards.length; i++) {
      if (collectionCardHasColour(cards[i], colour)) keep.push(cards[i]);
    }
    if (!keep.length) return 0;

    for (var j = 0; j < cards.length; j++) {
      if (keep.indexOf(cards[j]) === -1) cards[j].remove();
    }
    return keep.length;
  }

  /* Retry the search with the canonical word, marked so it never loops. */
  function redirectToSynonym(typed) {
    var alt = rewrite(typed);
    if (!alt) return false;

    var next = setParam(setParam(location.pathname + location.search, 'q', alt), MARKER, typed);
    location.replace(next);
    return true;
  }

  /*
   * Bring the material's products onto the results page. When the search found
   * something, the cards are appended to the grid it already built. When it
   * found nothing the page has no grid at all — only the "no results" panel —
   * so the filtered collection's own grid is lifted in whole, inline styles
   * and all, and the panel is reworded to say what is being shown.
   */
  function appendMaterial(read, typed) {
    return fabricCards(read)
      .then(function (doc) {
        if (!doc) return 0;
        var grid = document.querySelector('.pcx__grid');
        if (grid) return appendToGrid(grid, doc);
        return liftGrid(doc);
      })
      .catch(function () { return 0; });
  }

  /*
   * The fabric, as the results page shows it: the collection section alone
   * (a tenth the bytes of the page), every style in one page, one card per
   * colourway — and, when a colour was asked for, narrowed to it against the
   * colourways each card carries, since colour is not a filter the storefront
   * exposes. Resolves to the parsed document, or null when nothing survives.
   */
  function fabricCards(read) {
    return fetchCards(materialUrl(read.material, COLLECTION_SECTION))
      .then(function (doc) {
        if (doc) return doc;
        return fetchCards(materialUrl(read.material));
      })
      .then(function (doc) {
        if (!doc) return null;
        if (read.colour &&
            !narrowToColour(doc, read.colour, 'article.pcx-card', collectionCardHasColour)) {
          return null;
        }
        return doc;
      })
      .catch(function () { return null; });
  }

  /* Resolves to a parsed document, or null when it holds no cards. */
  function fetchCards(url) {
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('article.pcx-card') ? doc : null;
      })
      .catch(function () { return null; });
  }

  function appendToGrid(grid, doc) {
    var seen = {};
    grid.querySelectorAll('article.pcx-card').forEach(function (card) {
      var key = cardKey(card);
      if (key) seen[key] = true;
    });

    var order = 900;
    var added = 0;

    doc.querySelectorAll('article.pcx-card').forEach(function (card) {
      var handle = cardKey(card);
      if (!handle || seen[handle]) return;
      seen[handle] = true;
      card.style.order = String(order++);
      grid.appendChild(document.importNode(card, true));
      added += 1;
    });

    return added;
  }

  function liftGrid(doc) {
    var block = doc.querySelector('.pcx');
    var empty = document.querySelector('.empty-state');
    if (!block || !empty) return 0;

    /* The collection's own title, sort bar and endless-scroll rig go with it. */
    block.querySelectorAll(
      '.pcx__header, .pcx__nav-row, .pcx-filterbar, .pcx__sentinel, .pcx__loading'
    ).forEach(function (node) { node.remove(); });

    /* Sold-out cards are hidden while a collection has a next page. */
    var grid = block.querySelector('.pcx__grid');
    if (grid) grid.setAttribute('data-next-page', '');

    /*
     * A search that found nothing never renders the product grid, so its CSS
     * is not on the page either — the section's own <style> comes along, or
     * the cards land as a single unstyled column.
     */
    if (!document.querySelector('[data-jj-material-css]')) {
      doc.querySelectorAll('style').forEach(function (tag) {
        tag.setAttribute('data-jj-material-css', '');
        document.head.appendChild(document.importNode(tag, true));
      });
    }

    /* No spacing of its own: the heading's padding above and the lifted
       block's padding below are in assets/main-search-custom.css. */
    var host = document.createElement('div');
    host.appendChild(document.importNode(block, true));

    /*
     * The products go where the no-results panel stood, and the panel goes:
     * it says nothing was found, which is no longer true, and the heading
     * above already says what is being shown.
     *
     * Two panels can be standing here. When the search found nothing at all
     * the panel has a container of its own under the heading's, and the
     * whole container goes. When Shopify found something the theme could not
     * draw — "plum wool" matches on variant titles, then every card is
     * filtered out — the panel sits inside the results tab, in the SAME
     * container as the heading, and taking that container would take the
     * heading with it. There the tab block goes and the products follow the
     * heading directly.
     */
    var head = document.querySelector('.jj-search-head');
    var container = empty.closest('.container');

    if (container && head && container.contains(head)) {
      head.insertAdjacentElement('afterend', host);
      var tabs = empty.closest('.main-search');
      if (tabs) tabs.remove(); else empty.remove();
    } else {
      host.className = 'container';
      (container || empty).insertAdjacentElement('afterend', host);
      (container || empty).remove();
    }

    return block.querySelectorAll('article.pcx-card').length;
  }

  /*
   * Pull in each widening term's own results page and append any card the
   * grid does not already show. Deliberately defensive: a failure anywhere
   * leaves the page exactly as it rendered.
   */
  function appendWidened(extra) {
    var first = document.querySelector('article.pcx-card');
    if (!first || !first.parentElement) return;

    var grid = first.parentElement;
    var seen = {};
    grid.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var m = /\/products\/([a-z0-9-]+)/.exec(a.getAttribute('href') || '');
      if (m) seen[m[1]] = true;
    });

    var order = 900;

    extra.forEach(function (term) {
      fetch(root() + 'search?q=' + encodeURIComponent(term))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          doc.querySelectorAll('article.pcx-card').forEach(function (card) {
            var link = card.querySelector('a[href*="/products/"]');
            var m = link && /\/products\/([a-z0-9-]+)/.exec(link.getAttribute('href') || '');
            if (!m || seen[m[1]]) return;
            seen[m[1]] = true;
            card.style.order = String(order++);
            grid.appendChild(card);
          });
        })
        .catch(function () {});
    });
  }

  /* ------------------------------------------------------------------ init */

  patchFetch();
  markPending();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleResultsPage);
  } else {
    handleResultsPage();
  }
})();
